import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  DynamicFormInputComponent,
  NotificationService,
} from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { defaultFaqListData, FaqListData, QaData } from '../ui-types';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';

@Component({
  template: `
    <vdr-card>
      <!-- Default Display Count -->
      <vdr-form-field [label]="'Default Display Count'" class="mb-2">
        <input type="number" [formControl]="defaultDisplayCountControl" />
      </vdr-form-field>

      <!-- QA List -->
      <vdr-form-field [label]="'QA List'">
        <vdr-dynamic-form-input
          #qaListInput
          [control]="qaListControl"
          [def]="qaInputDef"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class FaqListFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  @ViewChild('qaListInput') qaListInput: DynamicFormInputComponent;
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  faqListData: FaqListData;

  defaultDisplayCountControl = new FormControl();
  qaListControl = new UntypedFormControl();
  qaInputDef: any = {
    type: 'string',
    list: true,
    ui: {
      component: 'question-answer-input',
      args: {},
    },
  };

  constructor(
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.qaInputDef = {
      ...this.qaInputDef,
      ui: {
        ...this.qaInputDef.ui,
        args: (this.config as any)?.ui?.args ?? {},
      },
    };
    this.faqListData = safeJSONParse<FaqListData>(
      this.formControl.value,
      this.notificationService,
      defaultFaqListData,
    )!;
    this.initializeFormControls();
  }

  ngAfterViewInit() {
    // Subscribe to value changes for the Default Display Count and Title fields.
    this.defaultDisplayCountControl.valueChanges.subscribe(this.onDefaultDisplayCountChange);

    // Register dynamic input changes for the QA list.
    if (this.qaListInput) {
      this.qaListInput.registerOnChange(this.onQaListChange);
      const qaDataList = this.faqListData?.qaList?.map(value => JSON.stringify(value)) || [];
      this.qaListInput.writeValue(qaDataList);
    }
  }

  // Initialize form controls with values from faqListData.
  private initializeFormControls() {
    this.defaultDisplayCountControl.setValue(this.faqListData?.defaultDisplayCount);
    const qaDataList = this.faqListData?.qaList?.map(value => JSON.stringify(value)) || [];
    this.qaListControl.setValue(qaDataList);
  }

  // Value change handler for the Default Display Count field.
  private onDefaultDisplayCountChange = (value: number) => {
    if (this.faqListData?.defaultDisplayCount !== value) {
      this.faqListData!.defaultDisplayCount = value;
      this.updateJsonString();
    }
  };

  // Value change handler for the QA List.
  private onQaListChange = (values: string[]) => {
    if (JSON.stringify(this.faqListData?.qaList) !== JSON.stringify(values)) {
      const defaultQaData: QaData = {
          question: { format: 'plain', i18n: { [this.config?.ui?.args?.activeLanguageCode ?? 'en']: '' } },
          answer: { format: 'plain', i18n: { [this.config?.ui?.args?.activeLanguageCode ?? 'en']: '' } },
      };
      this.faqListData!.qaList = values?.map(qaData => safeJSONParse<QaData>(
        qaData,
        this.notificationService,
        defaultQaData
      )).filter(notNullOrUndefined) ?? [];
      this.updateJsonString();
    }
  };

  // Update the JSON string and form control value.
  private updateJsonString() {
    const jsonString = JSON.stringify(this.faqListData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
